import { useMemo } from "react"

import { useTranslation } from "@bounce/i18n"

import { useFAQs as useLuggageStorageFAQs } from "@/components/LuggageStorage/FAQs/useFAQs"
import type { CityData } from "@/libs/bounce/cities"
import type { BoilerplateJSON, FAQ, SafeguardFaq } from "@/utils/boilerplate"
import { buildFaqs } from "@/utils/boilerplate"
import { getPreFaq } from "@/utils/faqs"

type PlaceInterpolations = {
  price: string
  url: string
  poi: string
}

const safeguardPlace: SafeguardFaq<PlaceInterpolations> = arg => arg

type UseFAQsOptions = {
  boilerplateContent?: BoilerplateJSON | null
  city: Pick<CityData, "translatedName" | "slug">
  interpolations: PlaceInterpolations
}

export const useFAQs = ({ city, interpolations, boilerplateContent }: UseFAQsOptions): FAQ[] => {
  const { t } = useTranslation("marketing-ls-poi")
  const luggageStorageFaqs = useLuggageStorageFAQs({ place: city })

  return useMemo(() => {
    const getLuggageStorageFaq = (faqId: string) => getPreFaq<PlaceInterpolations>(faqId, luggageStorageFaqs)

    const faqsStrings = [
      safeguardPlace({
        id: "Where can I find luggage storage near {{poi}}?",
        key: "whereCanIFindLuggageNear",
        question: int =>
          t("cmp.poiFaqs.whereCanIFindLuggageNearOptimized.question", "How can I get luggage storage at {{poi}}?", int),
        answer: int =>
          t(
            "cmp.poiFaqs.whereCanIFindLuggageNear.answer",
            "To find a convenient luggage storage option near {{poi}}, use the Bounce app or website to tell us how many bags you have and when you need storage. We'll show you all of your options so you can enjoy your day near {{poi}} without your bags.",
            int,
          ),
        structuredAnswer: int =>
          t(
            "cmp.poiFaqs.whereCanIFindLuggageNear.structuredAnswer",
            "<p>To find a convenient luggage storage option near {{poi}}, use the Bounce app or website to tell us how many bags you have and when you need storage. We'll show you all of your options so you can enjoy your day near {{poi}} without your bags. <a href='{{url}}'>Click here</a> to see them</p>",
            int,
          ),
        requires: [],
      }),
      safeguardPlace({
        id: "How much does luggage storage cost near {{poi}}?",
        key: "howMuchDoesLuggageStorageCostNearPoi",
        question: int =>
          t(
            "cmp.poiFaqs.howMuchDoesLuggageStorageCostNearPoi.question",
            "How much does luggage storage cost near {{poi}}?",
            int,
          ),
        answer: int =>
          t(
            "cmp.poiFaqs.howMuchDoesLuggageStorageCostNearPoi.answer",
            "We charge from {{price}} per day (24 hours) for luggage storage near {{poi}}.",
            int,
          ),
        structuredAnswer: int =>
          t(
            "cmp.poiFaqs.howMuchDoesLuggageStorageCostNearPoi.structuredAnswer",
            "<p>We charge from {{price}} per day (24 hours) for luggage storage near {{poi}}. <a href='{{url}}'>Click here</a> to see luggage storage locations</p>",
            int,
          ),
        requires: [],
      }),
      safeguardPlace(getLuggageStorageFaq("How does Bounce luggage storage work?")),
      safeguardPlace({
        id: "Where is my luggage stored?",
        key: "whereIsLuggageStored",
        question: () =>
          t("cmp.luggageStorageFaqs.whereIsLuggageStored.question", "Where is my luggage stored?", { ns: "marketing" }),
        answer: () =>
          t(
            "cmp.luggageStorageFaqs.whereIsLuggageStored.answer",
            "We only partner with trusted businesses who have a dedicated, secure place to keep your luggage safe. Some businesses may store luggage behind a counter that's off-limits to customers, while others may have storage rooms or closets for your luggage. But no matter where you choose to leave your luggage, our partners will keep it safe.",
            { ns: "marketing" },
          ),
        structuredAnswer: () =>
          t(
            "cmp.luggageStorageFaqs.whereIsLuggageStored.structuredAnswer",
            "<p>We only partner with trusted businesses who have a dedicated, secure place to keep your luggage safe. Some businesses may store luggage behind a counter that's off-limits to customers, while others may have storage rooms or closets for your luggage. But no matter where you choose to leave your luggage, our partners will keep it safe. <a href='{{url}}'>Click here</a> to see them</p>",
            { ns: "marketing" },
          ),
        requires: [],
      }),
      safeguardPlace(getLuggageStorageFaq("What can I store with Bounce?")),
      safeguardPlace({
        id: "Is there hourly luggage storage near {{poi}}?",
        key: "hourlyStorageNear",
        question: int =>
          t("cmp.poiFaqs.hourlyStorageNear.question", "Is there hourly luggage storage near {{poi}}?", int),
        answer: int =>
          t(
            "cmp.poiFaqs.hourlyStorageNear.answer",
            "Yes, but many companies charge per hour and costs can add up fast. We charge one price for 24 hours of storage, so storing your stuff is more affordable near {{poi}}.",
            int,
          ),
        structuredAnswer: int =>
          t(
            "cmp.poiFaqs.hourlyStorageNear.structuredAnswer",
            "<p>Yes, but many companies charge per hour and costs can add up fast. We charge one price for 24 hours of storage, so storing your stuff is more affordable near {{poi}}. <a href='{{url}}'>Click here</a> to see luggage storage locations</p>",
            int,
          ),
        requires: [],
      }),
      safeguardPlace(getLuggageStorageFaq("How long can I leave my luggage with Bounce?")),
      safeguardPlace({
        id: "How do I change or cancel my booking in {{poi}}?",
        key: "howDoIChangeOrCancelIn",
        question: int =>
          t("cmp.poiFaqs.howDoIChangeOrCancelIn.question", "How do I change or cancel my booking in {{poi}}?", int),
        answer: int =>
          t(
            "cmp.poiFaqs.howDoIChangeOrCancelIn.answer",
            "Plans change, we get it! If you need to change or cancel your booking in {{poi}} for any reason, the best way to do it is from your booking details page in the Bounce app. You can edit the dates, drop-off and pick-up times, and number of bags. You can also cancel your booking or rebook at a nearby store. If you can't find your booking details, check the email address you used to make your booking. You can change your booking by following the link there, or reach out to us if you need support.",
            int,
          ),
        requires: [],
      }),
    ]

    return buildFaqs({ faqs: faqsStrings, data: boilerplateContent ?? {}, interpolations, t })
  }, [boilerplateContent, interpolations, luggageStorageFaqs, t])
}
